/**
 * storage.ts — the ONE shared image-upload util.
 *
 * Phase 2a of the upload consolidation. Every image-upload site (contact/company
 * photo, employee profile photo, user avatar) will route through here so there is
 * a single upload path, a single bucket, and a single RLS contract — instead of
 * today's scattered recordAvatars.ts / fileFields.ts / hand-rolled copies.
 *
 * This is the UNION of the good parts of the two existing helpers:
 *   • from recordAvatars.ts — the public "record-avatars" bucket, upsert:true
 *     (replace-in-place), the cache-busted public URL, the friendly missing-bucket
 *     message, and the workspace-first path that the 0105 storage policy scopes on.
 *   • from fileFields.ts   — the RECORD-LESS path key, so an image can be uploaded
 *     DURING create (before the record exists) under a stable client key, exactly
 *     the pattern Lis chose for upload-during-create.
 *
 * PATH SCHEME (matches the 0105 policy's foldername[1] = workspace scoping):
 *   <workspace_id>/<module>/<recordId-or-clientKey>/<field>.<ext>
 *
 * As of 2b this is the CANONICAL source: it owns the bucket name and the URL→path
 * parser (no longer imported from recordAvatars — that dependency was inverted).
 * recordAvatars.ts now keeps only its avatar-URL-map loader for the list pages.
 *
 * Fail-loud, per the app-wide law: every path throws a clear Error on failure —
 * nothing is silently swallowed. Callers surface it (the component does).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { queryClient } from './queryClient';
import { apiConfigured, requireWorkspaceId } from './apiClient';
import { apiOnlyRead } from './apiBridge';
import { uploadFile, deleteStoredFile } from './uploads';

/**
 * The public images bucket. CANONICAL HERE (2b): the bucket name and the URL→path
 * parser used to live in recordAvatars.ts and were imported from there in 2a. That
 * dependency is now inverted — storage.ts owns them, and recordAvatars keeps only
 * its avatar-URL-map loader.
 */
export const AVATAR_BUCKET = 'record-avatars';

/** Max image size — mirrors the record-avatars bucket's 5 MB file_size_limit. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** File-input accept string — mirrors the bucket's allowed_mime_types (image/*). */
export const IMAGE_ACCEPT = 'image/*';

/** Derive the storage object path from a public image URL ('' if it isn't ours). */
export function pathFromImageUrl(url: string): string {
  const marker = `/${AVATAR_BUCKET}/`;
  const i = url.indexOf(marker);
  if (i < 0) return '';
  return url.slice(i + marker.length).split('?')[0];
}

// ── SIGNED URLS ───────────────────────────────────────────────
// Migration 0170 scoped the storage buckets to the caller. `record-avatars` is
// STILL PUBLIC today; 0171 flips it to private. Signed URLs work against either,
// which is exactly why this lands first and the flip lands after — there is no
// window where anything is broken.
//
// From here on the value stored against a record is a PATH. Values written
// before this change are full public URLs and 14 of them exist in production, so
// every read goes through `storagePathOf()`, which accepts both. Nothing is
// migrated and no backfill is needed.

/** How long a minted URL lives. Matches fileFields.ts so the app has ONE TTL. */
export const SIGNED_URL_TTL_SECONDS = 3600;            // 1 hour

/**
 * How often a MOUNTED view re-mints. MUST be shorter than the TTL, and this is
 * the trap worth spelling out:
 *
 * React Query's `staleTime` decides whether a refetch is ALLOWED on a trigger
 * (mount, window focus, reconnect). It does NOT schedule one. A list left open
 * on a second monitor never remounts and never refocuses, so without an interval
 * every <img src> would be a dead token an hour later while the page still looks
 * perfectly fine. 30 minutes gives two mints per TTL.
 */
export const SIGNED_URL_REFRESH_MS = 30 * 60 * 1000;   // 30 min = half the TTL
export const SIGNED_URL_STALE_MS   = 30 * 1000;        // 30 s

const isAbsoluteUrl = (v: string): boolean => /^https?:\/\//i.test(v);

/**
 * The storage path for a stored image value, or '' when the value isn't ours.
 *
 * Handles all four shapes that reach it: a bare path (what we store now), a
 * public URL (what 14 production rows still hold), a signed URL (re-mintable),
 * and an EXTERNAL url — a gravatar, or a MinIO link from the Phase-2 backend
 * bridge — for which it returns '' so the caller passes the value through
 * untouched instead of trying to sign someone else's host.
 */
export function storagePathOf(value: string | null | undefined): string {
  if (!value) return '';
  const v = value.trim();
  if (!v) return '';
  if (isAbsoluteUrl(v)) return pathFromImageUrl(v);   // '' when it isn't ours
  return v.split('?')[0];
}

/** Batch-mint signed URLs. Returns path -> signed URL. Fail-soft; never throws. */
export async function signPaths(
  bucket: string,
  paths: string[],
  ttl: number = SIGNED_URL_TTL_SECONDS,
): Promise<Record<string, string>> {
  const unique = Array.from(new Set(paths.filter(Boolean)));
  if (unique.length === 0) return {};

  // Self-hosted: mint each URL through OUR backend (MinIO presigns them).
  // Without this the call lands on the supabase.storage shim, which returns an
  // empty list with NO error — every image would silently render blank.
  if (apiConfigured()) {
    const out: Record<string, string> = {};
    await Promise.all(
      unique.map(async (path) => {
        try {
          const dl = await apiOnlyRead<{ url: string }>(
            `/storage/download-url?key=${encodeURIComponent(path)}&expires_in=${ttl}`,
            (raw) =>
              raw && typeof raw === 'object' && typeof (raw as { url?: unknown }).url === 'string'
                ? { url: (raw as { url: string }).url }
                : null,
          );
          if (dl.served && dl.data.url) out[path] = dl.data.url;
        } catch {
          /* fail-soft, per this function's contract — a missing thumbnail
             must never break the page it sits on */
        }
      }),
    );
    return out;
  }

  const { data, error } = await supabase.storage.from(bucket).createSignedUrls(unique, ttl);
  if (error) {
    console.warn(`[storage] createSignedUrls failed for ${bucket}:`, error.message);
    return {};
  }
  const out: Record<string, string> = {};
  for (const row of data ?? []) {
    if (row.path && row.signedUrl) out[row.path] = row.signedUrl;
  }
  return out;
}

/** Query key for one minted URL — exported so callers can invalidate it. */
export const signedUrlKey = (bucket: string, path: string) =>
  ['signed_url', bucket, path] as const;

/**
 * One signed URL for one stored value.
 *
 * Keyed on the PATH, so every component showing the same image shares a single
 * mint rather than issuing one request each — the roster pages render the same
 * avatar many times over. Returns the value unchanged when it is an external
 * URL, and null while the first mint is in flight (callers show initials).
 */
export function useSignedUrl(bucket: string, value: string | null | undefined): string | null {
  const path = storagePathOf(value);
  const passthrough = Boolean(value) && !path;

  const q = useQuery({
    queryKey: signedUrlKey(bucket, path),
    queryFn: async () => (await signPaths(bucket, [path]))[path] ?? null,
    enabled: Boolean(path),
    staleTime: SIGNED_URL_STALE_MS,
    refetchInterval: SIGNED_URL_REFRESH_MS,
    refetchIntervalInBackground: false,
    gcTime: SIGNED_URL_TTL_SECONDS * 1000,
  });

  if (passthrough) return value as string;
  return q.data ?? null;
}

/** `useSignedUrl` for the record-avatars bucket — the common case. */
export function useSignedImage(value: string | null | undefined): string | null {
  return useSignedUrl(AVATAR_BUCKET, value);
}

/**
 * Drop the cached mint for a path. Needed after an upsert replace: the path is
 * unchanged, so without this the cached URL (and the browser's cache of it)
 * would keep showing the OLD image.
 */
export function invalidateSignedUrl(bucket: string, path: string): void {
  if (!path) return;
  void queryClient.invalidateQueries({ queryKey: signedUrlKey(bucket, path) });
}

export interface UploadImageParams {
  /** Owning module key, e.g. 'crm_contacts' / 'crm_companies' / 'profiles'. */
  module: string;
  /** Field key used as the object filename, e.g. 'photo' / 'logo' / 'avatar'. */
  field: string;
  /**
   * The path's owner segment: the real record id (record view) OR a stable
   * pre-save client key (create flow — see {@link newClientKey}). Either works;
   * the 0105 policy only scopes on the FIRST folder (the workspace), so both pass.
   */
  key: string;
  /** The image to upload. Validated against image/* and 5 MB before the network. */
  file: File;
  /** Workspace override; defaults to the signed-in person's workspace. */
  workspaceId?: string;
}

/**
 * A stable, URL-safe key for the CREATE flow — lets a user pick a photo before the
 * record exists. Mirrors fileFields.ts's randomId. Generate ONCE and keep it
 * stable (a ref) so re-uploads replace-in-place instead of orphaning objects.
 */
export function newClientKey(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Best-guess extension from a File (name first, then MIME, else 'png'). */
function extOf(file: File): string {
  const m = /\.([a-z0-9]+)$/i.exec(file.name);
  if (m) return m[1].toLowerCase();
  const mime = (file.type.split('/')[1] ?? '').toLowerCase();
  return mime || 'png';
}

/**
 * Build the storage object key. Exported for tests / callers that need the key.
 *
 * `avatars/<workspace_id>/<module>/<recordId-or-clientKey>/<field>.<ext>` since
 * 2026-10-17: the backend accepts only `<domain>/<the caller's workspace>/…`
 * keys, and the old Supabase-era shape (`<workspace_id>/<module>/…`) has no
 * domain, so every own-stack image upload was refused with a 400.
 */
export function imagePath(p: { workspaceId?: string; module: string; key: string; field: string; ext: string }): string {
  // Falls back to the signed-in person's workspace, not a constant. Existing images keep
  // the value already stored against their record — nothing is re-pathed or moved.
  const ws = p.workspaceId || requireWorkspaceId();
  return `avatars/${ws}/${p.module}/${p.key}/${p.field}.${p.ext}`;
}

/** Throw a clear, user-facing Error if the file isn't a ≤5 MB image. */
function assertImage(file: File): void {
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error('That file isn’t an image. Please choose a PNG, JPG, or GIF.');
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error('That image is larger than 5 MB. Please choose a smaller file.');
  }
}

/**
 * Upload an image and return its storage KEY — not a URL.
 *
 * The key is the identifier and is what a record persists; the URL is minted at
 * render by `useSignedImage`. Re-uploading to the same (module, key, field)
 * replaces the object in place.
 *
 * ── MINIO ONLY (2026-10-17) ────────────────────────────────────────────────
 * This used to try MinIO and, on ANY failure, fall back to Supabase Storage —
 * which on this branch is a shim that always errors. Worse, its MinIO branch
 * returned a one-hour presigned DOWNLOAD URL, so a photo that did upload was
 * stored as a link that died an hour later. Now: one path, `uploadFile()` in
 * uploads.ts, and any failure throws a message fit to show.
 *
 * Throws on: a non-image or oversize file, no backend configured, the presign
 * refused, the PUT refused, or the network. Never returns on failure.
 */
export async function uploadImage(params: UploadImageParams): Promise<string> {
  const { module, field, key, file, workspaceId } = params;
  if (!key) throw new Error('uploadImage: a recordId or clientKey is required for the storage path.');
  assertImage(file);

  const path = imagePath({ workspaceId, module, key, field, ext: extOf(file) });
  const stored = await uploadFile(file, { key: path });

  // A replace reuses the SAME key, so any previously minted URL still points at
  // the old bytes until its cache entry is dropped.
  invalidateSignedUrl(AVATAR_BUCKET, stored.key);
  return stored.key;
}

/**
 * Delete a previously uploaded image by its stored value. Idempotent for an empty
 * value; throws if the value isn't one of ours or the delete fails.
 *
 * MinIO only (2026-10-17) — see `uploadImage`. A value from the Supabase era (a
 * public URL, or a path with no `avatars/` domain) is not an object in MinIO;
 * the backend refuses it and this throws, so the caller says the file could not
 * be deleted rather than claiming it was.
 */
export async function removeImage(value: string): Promise<void> {
  if (!value || !value.trim()) return; // nothing to remove — not an error
  const path = storagePathOf(value);
  if (!path) throw new Error('removeImage: that value doesn’t point at the images bucket.');

  await deleteStoredFile(path);
  // A deleted image must not keep resolving from the signed-URL cache.
  invalidateSignedUrl(AVATAR_BUCKET, path);
}
